
import React, { useState, useEffect } from 'react';
import { SponsoredService } from '../services/sponsoredService';
import { X, Play, ShieldAlert, Sparkles, Timer, ChevronRight } from 'lucide-react';
import { useAuth } from './AuthBridge';
import { useNavigate } from 'react-router';

import { AdUnit } from './AdUnit';
import { useThemeTokens } from '../hooks/useActiveTheme';

interface AdIntervalBreakProps {
  onComplete: () => void;
  moduleName: string;
}

export const AdIntervalBreak: React.FC<AdIntervalBreakProps> = ({ onComplete, moduleName }) => {
  const { user, activeCert } = useAuth();
  const navigate = useNavigate();
  const tokens = useThemeTokens();
  const [config, setConfig] = useState(SponsoredService.getConfig());
  const skipThreshold = config.intervals.intermissionSkipSeconds ?? 5;
  
  const [timeLeft, setTimeLeft] = useState(skipThreshold);
  const [canSkip, setCanSkip] = useState(skipThreshold <= 0);

  useEffect(() => {
    const handleUpdate = () => setConfig(SponsoredService.getConfig());
    window.addEventListener('sponsored-config-updated', handleUpdate);
    
    // Ensure we have the latest config from the server on mount
    SponsoredService.fetchLatestConfig().then(latest => {
      setConfig(latest);
    });
    
    return () => window.removeEventListener('sponsored-config-updated', handleUpdate);
  }, []);

  useEffect(() => {
    // Reset viewport to top for mobile visibility
    window.scrollTo(0, 0);
    
    // Prevent background scrolling
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';
    
    return () => {
        document.body.style.overflow = originalStyle;
    };
  }, []);

  useEffect(() => {
    if (timeLeft <= 0) {
      setCanSkip(true);
      return;
    }
    const timer = setInterval(() => setTimeLeft(prev => prev - 1), 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);

  const placementConfig = config.placements.overlay;
  const isNetworkSource = placementConfig?.source === 'network';

  if (user?.planTier === 'paid' || !config.isActive || !config.intermissionEnabled) {
    onComplete();
    return null;
  }

  const handleUpgrade = () => {
    navigate(`/${activeCert?.id || user?.activeCertId || 'cna'}/elite`);
  };

  return (
    <div className={`fixed inset-0 z-[1000] ${tokens.heroBannerBg} backdrop-blur-3xl flex flex-col items-center justify-start md:justify-center p-3 sm:p-6 md:p-12 pt-safe pb-safe animate-fade-in overflow-y-auto custom-scrollbar`}>
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-30">
        <div className="absolute top-[10%] right-[10%] w-[40%] h-[40%] bg-indigo-500/30 blur-[120px] rounded-full animate-pulse"></div>
        <div className="absolute bottom-[10%] left-[10%] w-[40%] h-[40%] bg-medical-500/30 blur-[120px] rounded-full animate-pulse [animation-delay:1s]"></div>
      </div>

      <div className="max-w-4xl w-full my-auto space-y-6 sm:space-y-8 md:space-y-10 relative z-10 text-center py-4 md:py-0">
        <div className="flex flex-col items-center gap-3 md:gap-4 animate-slide-up">
          <div className="p-3 md:p-4 bg-white/10 backdrop-blur-md rounded-2xl md:rounded-[2rem] border border-white/20 text-white shadow-2xl ring-1 ring-white/10">
            <Timer size={28} className={`md:w-9 md:h-9 ${!canSkip ? 'animate-pulse' : ''}`} />
          </div>
          <div>
            <h2 className="text-xl sm:text-2xl md:text-4xl font-black text-white tracking-tight leading-none">Clinical Intermission</h2>
            <p className="text-white/50 text-[9px] md:text-xs font-black tracking-[0.3em] mt-2 flex items-center justify-center gap-2 md:gap-3 uppercase">
              <span className="w-6 md:w-8 h-px bg-white/20"></span>
              Logic Sync In Progress
              <span className="w-6 md:w-8 h-px bg-white/20"></span>
            </p>
          </div>
        </div>

        <div className="w-full max-w-4xl mx-auto">
          {isNetworkSource ? (
            <div className="bg-white rounded-2xl md:rounded-[2.5rem] p-2 sm:p-4 md:p-8 shadow-2xl border border-slate-100 animate-pop overflow-hidden">
               <AdUnit placement="overlay" />
            </div>
          ) : (
            <div className="bg-white rounded-2xl md:rounded-[2.5rem] overflow-hidden shadow-2xl border border-slate-100 group animate-pop">
              <div className="grid grid-cols-1 md:grid-cols-2">
                <div className="aspect-video md:aspect-auto bg-slate-950 relative overflow-hidden min-h-[160px]">
                   {config.type === 'video' ? (
                     <iframe 
                        src={`${config.mediaUrl}${config.mediaUrl.includes('?') ? '&' : '?'}autoplay=1&mute=1`} 
                        className="w-full h-full border-none pointer-events-none" 
                     />
                   ) : (
                     <img src={config.mediaUrl} className="w-full h-full object-cover transition-transform duration-[10s] group-hover:scale-105" alt="Ad" />
                   )}
                   <div className="absolute inset-0 bg-gradient-to-t from-slate-950/40 to-transparent pointer-events-none"></div>
                </div>
                <div className="p-4 sm:p-6 md:p-10 flex flex-col justify-center text-left bg-white text-slate-900 relative">
                  <div className="flex items-center gap-2 mb-2 sm:mb-4">
                     <div className="p-1 bg-indigo-50 text-indigo-600 rounded-md">
                        <Sparkles size={14} />
                     </div>
                     <span className="text-[9px] md:text-[10px] font-black text-slate-400 tracking-widest uppercase">Educational Partner</span>
                  </div>
                  <h3 className="text-lg sm:text-2xl md:text-3xl font-black leading-snug mb-2 md:mb-4 tracking-tight text-slate-900 break-words">{config.title}</h3>
                  <p className="text-xs sm:text-sm md:text-base text-slate-500 font-medium mb-4 sm:mb-6 leading-relaxed opacity-90 break-words">"{config.description}"</p>
                  
                  <div className="mt-auto pt-2">
                    <a 
                        href={config.targetUrl} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-6 py-3 sm:px-8 sm:py-4 bg-slate-950 text-white rounded-xl md:rounded-2xl font-black text-[11px] sm:text-xs tracking-widest hover:bg-indigo-600 transition-all shadow-xl active:scale-95 border-b-2 sm:border-b-4 border-black group/btn"
                    >
                        <span>{config.ctaText || 'Learn More'}</span> 
                        <ChevronRight size={16} className="group-hover/btn:translate-x-1 transition-transform shrink-0" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col items-center gap-4 sm:gap-6 animate-slide-up">
           {!canSkip ? (
             <div className="flex flex-col items-center gap-3">
                <div className="relative w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center">
                   <svg className="w-full h-full rotate-[-90deg] drop-shadow-2xl">
                      <circle cx="50%" cy="50%" r="42%" stroke="currentColor" strokeWidth="5" fill="transparent" className="text-white/10" />
                      <circle 
                          cx="50%" cy="50%" r="42%" stroke="currentColor" strokeWidth="5" fill="transparent" 
                          className="text-indigo-400"
                          strokeDasharray="100"
                          strokeDashoffset={100 - (100 * (timeLeft / skipThreshold))}
                          strokeLinecap="round"
                          style={{ transition: 'stroke-dashoffset 1s linear' }}
                      />
                   </svg>
                   <span className="absolute text-white font-black text-lg sm:text-xl tabular-nums">{timeLeft}</span>
                </div>
                <p className="text-[9px] md:text-[10px] font-black text-white/50 tracking-widest uppercase">Resuming Sequence Shortly</p>
             </div>
           ) : (
             <button 
                onClick={onComplete}
                className="w-full sm:w-auto group flex items-center justify-center gap-3 px-8 py-4 sm:px-12 sm:py-5 bg-white text-slate-950 rounded-xl md:rounded-2xl font-black text-xs sm:text-sm tracking-widest shadow-2xl hover:bg-emerald-50 transition-all active:scale-95 animate-pop border-b-4 border-slate-200"
             >
                <span>Return to {moduleName}</span> 
                <Play size={18} className="fill-current group-hover:scale-110 transition-transform shrink-0" />
             </button>
           )}

           <button 
             onClick={handleUpgrade} 
             className="flex items-center gap-2 text-white/40 hover:text-white transition-all text-[9px] sm:text-[10px] font-black tracking-widest group px-4 py-1.5 rounded-full border border-white/10 hover:bg-white/5 min-h-[32px]"
           >
             <ShieldAlert size={13} className="group-hover:scale-110 transition-transform text-indigo-400 shrink-0" /> 
             <span>Go Ad-Free with Elite Pro</span>
           </button>
        </div>
      </div>
    </div>
  );
};
